import { useMemo } from "react";
import Button from "@shared/components/ui-kit/form/Button/Button.web";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Alert from "@ui-kit/feedback/alerts/Alert/Alert.web";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import { useTranslation, Trans } from "@shared/utils/internationalization";
import kebabCase from "lodash/kebabCase";
import { enums, enumsTypes } from "@shared/constants";
import type { UpgradeOfferSharedProps } from "./UpgradeOffer.props";

const PRICE_PLACEHOLDER = "{{price_difference}}";

const FormattedPrice = ({
  value,
  currency,
}: {
  value: number;
  currency: enumsTypes.CurrencyMapIdType;
}): JSX.Element => (
  <CurrencyFormat
    value={value}
    currency={currency ?? enums.CURRENCY_MAP.CAD.id}
    displayType="text"
    data-testid="upgrade-offer-price"
    fixedDecimalScale={false}
    autoDecimalScale
  />
);

/**
 * Renders a custom admin-configured template, splitting around
 * `{{price_difference}}` so the formatted currency component can be inlined.
 */
function TemplateDescription({
  template,
  name,
  upgradeAmount,
  currency,
}: {
  template: string;
  name: string;
  upgradeAmount: number;
  currency: enumsTypes.CurrencyMapIdType;
}): JSX.Element {
  const parts = useMemo(() => {
    const withName = template.replace(/\{\{to_item_name\}\}/g, name);
    const idx = withName.indexOf(PRICE_PLACEHOLDER);
    if (idx === -1) return { before: withName, after: null };
    return {
      before: withName.slice(0, idx),
      after: withName.slice(idx + PRICE_PLACEHOLDER.length),
    };
  }, [template, name]);

  if (parts.after === null) {
    return <>{parts.before}</>;
  }

  return (
    <>
      {parts.before}
      <FormattedPrice value={upgradeAmount} currency={currency} />
      {parts.after}
    </>
  );
}

export default function UpgradeOffer({
  name,
  upgradeAmount,
  itemType,
  currency,
  onUpgrade,
  dataTestId,
  isLoading,
  messageTemplate,
}: UpgradeOfferProps): JSX.Element {
  const { t } = useTranslation("purchases");

  return (
    <Alert
      justifyContent="space-between"
      colorPalette="purple"
      data-testid={dataTestId ?? `upgrade-offer-alert-${kebabCase(name)}`}
    >
      <Flex alignItems="flex-start">
        <Alert.Icon
          icon="TicketStar"
          variant="solid"
          w="element.xl"
          h="element.xl"
          marginTop="2xs"
        />
        <Box>
          <Alert.Title>{t("Level up your experience!")}</Alert.Title>
          <Alert.Description>
            {messageTemplate ? (
              <TemplateDescription
                template={messageTemplate}
                name={name}
                upgradeAmount={upgradeAmount}
                currency={currency || "CAD"}
              />
            ) : (
              <Trans
                t={t}
                i18nKey="Upgrade to {{item}} for only <0>{{price}}</0> more per {{itemType}}."
                values={{
                  item: name,
                  itemType,
                  price: upgradeAmount,
                }}
                components={[
                  <FormattedPrice
                    key="price"
                    value={upgradeAmount}
                    currency={currency || "CAD"}
                  />,
                ]}
              />
            )}
          </Alert.Description>
        </Box>
      </Flex>
      <Button
        ml="xs"
        data-testid={`upgrade-offer-button-${kebabCase(name)}`}
        variant="outline"
        colorPalette="purple"
        onClick={onUpgrade}
        isLoading={isLoading}
        minWidth="fit-content"
      >
        {t("Upgrade")}
      </Button>
    </Alert>
  );
}

export type UpgradeOfferProps = UpgradeOfferSharedProps;
